import React, { FC, Key, useEffect, useMemo, useState } from 'react';
import { Button, Modal, Tree } from 'antd';
import { AntTreeNodeProps } from 'antd/es/tree';
import { SearchInput } from 'common/components';
import { ArrowDownShort, ArrowRightShort } from 'common/svg';
import {
  filterTreeDataBySearch,
  getAllTreeKeys,
  getItemKeyFromTreeKey,
  getTreeNodeParentKey,
  highlightTreeTitle,
} from 'common/helpers';
import { ISubdivisionItem } from 'entities/Subdivision/Subdivision.models';
import { buildSubdivisionTree } from 'entities/Subdivision/Subdivision.helper';

interface IComponentProps {
  open: boolean;
  loading: boolean;
  placeholder?: string;
  account?: string;
  subdivisions: ISubdivisionItem[];
  onCancel: () => void;
  onSubmit: (subdivisionId: number, onSuccess: () => void) => void;
}

export const SubdivisionTreeModal: FC<IComponentProps> = ({
  open,
  loading,
  placeholder = '',
  account = '',
  subdivisions,
  onCancel,
  onSubmit,
}) => {
  const [search, setSearch] = useState<string>('');
  const [autoExpandParent, setAutoExpandParent] = useState(true);
  const [expandedKeys, setExpandedKeys] = useState<Key[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<Key[]>([]);

  const treeData = useMemo(() => buildSubdivisionTree(subdivisions), [subdivisions]);

  useEffect(() => {
    const allKeys = getAllTreeKeys(treeData);
    setExpandedKeys(allKeys);
  }, [treeData]);

  const highlightedTreeData = useMemo(() => highlightTreeTitle(treeData, search), [search, treeData]);

  const handleSearch = async (value = ''): Promise<void> => {
    const matchedKeys = filterTreeDataBySearch(treeData, value);
    const newExpandedKeys = matchedKeys
      .map((key) => getTreeNodeParentKey(key, treeData))
      .filter((key, index, array): key is Key => key !== null && array.indexOf(key) === index);

    setSelectedKeys([]);
    setExpandedKeys(newExpandedKeys);
    setSearch(value);
    setAutoExpandParent(true);
  };

  const handleExpand = (keys: Key[]): void => {
    setExpandedKeys(keys);
    setAutoExpandParent(false);
  };

  const handleSelect = (keys: Key[]): void => {
    if (!keys.length) {
      return;
    }

    setSelectedKeys(keys);
  };

  const handleCancel = (): void => {
    if (loading) {
      return;
    }

    setSearch('');
    setSelectedKeys([]);
    onCancel();
  };

  const handleSubmit = (): void => {
    const key = getItemKeyFromTreeKey(selectedKeys[0]);
    onSubmit(Number(key), handleCancel);
  };

  return (
    <Modal
      rootClassName="redesign modal"
      width={400}
      centered
      open={open}
      onCancel={handleCancel}
      destroyOnClose
      closeIcon={false}
      styles={{ mask: { background: 'transparent' } }}
      footer={[
        <Button key="cancel" className="button-sm secondary" loading={loading} onClick={handleCancel}>
          Отмена
        </Button>,
        <Button
          key="submit"
          className="button-sm primary"
          disabled={!selectedKeys.length}
          loading={loading}
          onClick={handleSubmit}
        >
          Сохранить
        </Button>,
      ]}
    >
      <div className="mb-6">
        <SearchInput auto placeholder={placeholder} minLength={0} onSearch={handleSearch} />
      </div>

      {!!account.length && <div className="mb-6 ff-medium fs-xs color-bright-green">{account}</div>}

      <div className="mb-6" style={{ height: 360 }}>
        <Tree
          height={360}
          virtual
          treeData={highlightedTreeData}
          expandedKeys={expandedKeys}
          autoExpandParent={autoExpandParent}
          selectedKeys={selectedKeys}
          onExpand={handleExpand}
          onSelect={handleSelect}
          switcherIcon={(props: AntTreeNodeProps) => {
            return props.expanded ? (
              <ArrowDownShort className="icon-arrow-down-short-dark-grey" />
            ) : (
              <ArrowRightShort className="icon-arrow-right-short-dark-grey" />
            );
          }}
        />
      </div>
    </Modal>
  );
};
